import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import isEmpty from "lodash-es/isEmpty"
import isUndefined from "lodash-es/isUndefined"

import { CrossIcon } from "@/3514/assets"
import { Link } from "@/3514/components/shared/link/link.component"
import { SpecialChars } from "@/3514/components/shared/typography/special-char/special-char.config"
import { ESpecialChar } from "@/3514/components/shared/typography/special-char/special-char.types"
import { Typography } from "@/3514/components/shared/typography/typography.component"
import {
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight
} from "@/3514/components/shared/typography/typography.types"
import { buildCssClassName, getCssIdentifierFromFileName, renderRichContent, withPressHandler } from "@/3514/utils"
import { richTextRendererUtils } from "@/shared/rich-text-renderer/rich-text-renderer-utils"

import config from "./info-box.config"
import styles from "./info-box.styles"
import type { TInfoBoxProps as TProps } from "./info-box.types"

const {
    getClassesForRichText,
    cutSpacesFromHTMLString: cutSpaces,
    rendererAllowedTags: allowedTags,
    variantIcons
} = config

const displayName: string = "InfoBox"
const editorCssIdentifier: string = getCssIdentifierFromFileName(displayName, "ed")

const Box: FC<TProps> = ({
    variant,
    className = String(),
    title,
    message,
    isDismissable = undefined,
    onDismiss = undefined,
    content = undefined,
    action = undefined
}: TProps): ReactElement => (
    <div className={buildCssClassName(styles.variants[variant](), { [className]: className })}>
        <div className={styles.titleWrapper()}>
            {variantIcons[variant]}
            <Typography size={ETypographySize.Small} weight={ETypographyWeight.Bold} text={title} />
        </div>

        {!isEmpty(message) || !isEmpty(action) ? (
            <div>
                {!isEmpty(message) &&
                    (richTextRendererUtils.isStringPlainText(message) ? (
                        <Typography className={styles.hint()} size={ETypographySize.Small} text={message} />
                    ) : (
                        renderRichContent({
                            identifier: editorCssIdentifier,
                            classes: getClassesForRichText(editorCssIdentifier),
                            allowedTags
                        })(cutSpaces(message))
                    ))}

                {!isEmpty(action) && (
                    <Fragment>
                        {SpecialChars[ESpecialChar.EmptySpace]}

                        {"to" in action ? (
                            <Link
                                tabIndex={0}
                                to={action.to}
                                textWeight={action?.textWeight || ETypographyWeight.Default}
                            >
                                {action.children}
                            </Link>
                        ) : (
                            <div
                                role="link"
                                tabIndex={0}
                                className={styles.linkWrapper()}
                                onKeyDown={action.onClick}
                                onClick={action.onClick}
                                onFocus={action.onHover}
                                onMouseOver={action.onHover}
                            >
                                <Typography
                                    className={styles.linkText()}
                                    text={action.children}
                                    weight={action?.textWeight || ETypographyWeight.Default}
                                    size={ETypographySize.Small}
                                    tag={ETypographyTag.Span}
                                    color={ETypographyColor.Accent}
                                />
                            </div>
                        )}
                    </Fragment>
                )}
            </div>
        ) : null}

        {isDismissable && (
            <button
                type="button"
                aria-label="Dismiss info box"
                onClick={withPressHandler(onDismiss)}
                onKeyDown={withPressHandler(onDismiss)}
                tabIndex={0}
                className={styles.dismissButton({ variant })}
            >
                <CrossIcon strokeColor={config.variantDismissIconColor[variant]} />
            </button>
        )}

        {isUndefined(content) ? null : <div>{content}</div>}
    </div>
)

Box.displayName = displayName

export { Box as InfoBox }
